import {
  Clock3,
  History as HistoryIcon,
  MessageSquareText,
  RotateCcw,
  Trash2,
  X,
} from "lucide-react";
import {
  type MouseEvent,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Button,
  StatusPill,
} from "../../design/primitives";
import type {
  AvailableConversationEntry,
  ConversationSummary,
  RunSnapshot,
} from "./api";
import {
  conversationLabel,
  formatServerDateTime,
  historyFailureMessage,
  historyRoleLabel,
  runStateLabel,
  shortResourceId,
} from "./model";
import type { ConversationHistoryController } from "./useConversationHistory";

const ServerTime = ({
  value,
  className,
}: {
  value: string;
  className?: string;
}) => (
  <time className={className} dateTime={value} title={value}>
    {formatServerDateTime(value)}
  </time>
);

const Failure = ({
  code,
  onRetry,
}: {
  code: string | null;
  onRetry?: () => void;
}) => (
  <div className="history-failure" role="alert">
    <strong>Istoricul nu poate fi afișat</strong>
    <p>{historyFailureMessage(code)}</p>
    {code ? <code>{code}</code> : null}
    {onRetry ? (
      <Button variant="secondary" onClick={onRetry}>
        Reîncearcă
      </Button>
    ) : null}
  </div>
);

export const ConversationRail = ({
  history,
  run,
  onSelect,
}: {
  history: ConversationHistoryController;
  run: RunSnapshot | null;
  onSelect?: () => void;
}) => {
  const select = (conversation: ConversationSummary) => {
    void history.selectConversation(conversation);
    onSelect?.();
  };

  return (
    <div className="session-navigation">
      <div className="session-navigation__header">
        <div>
          <p className="eyebrow">Spațiu personal</p>
          <h2>Istoric</h2>
        </div>
      </div>

      {run ? (
        <div className="session-navigation__current">
          <span
            className="session-navigation__current-icon"
            aria-hidden="true"
          >
            <MessageSquareText size={18} />
          </span>
          <span>
            <strong>Rulare selectată</strong>
            <span>{runStateLabel(run.state)}</span>
            <code>{shortResourceId(run.run_id)}</code>
          </span>
        </div>
      ) : null}

      <div className="history-rail" aria-label="Conversații păstrate">
        <div className="history-rail__heading">
          <HistoryIcon size={16} aria-hidden="true" />
          <strong>Conversații</strong>
        </div>

        {history.conversationsState === "unavailable" ? (
          <div className="history-rail__empty">
            Istoricul apare numai după bootstrapul autorizat.
          </div>
        ) : history.conversationsState === "loading" &&
          history.conversations.length === 0 ? (
          <div className="history-rail__loading" role="status">
            <span aria-hidden="true" />
            Încărcăm istoricul autorizat
          </div>
        ) : history.conversationsState === "error" ? (
          <Failure
            code={history.conversationsErrorCode}
            onRetry={() => void history.refreshConversations()}
          />
        ) : history.conversations.length === 0 ? (
          <div className="history-rail__empty">
            <MessageSquareText size={20} aria-hidden="true" />
            <strong>Nu există conversații păstrate</strong>
            <span>
              O conversație apare aici numai după persistarea confirmată de
              API.
            </span>
          </div>
        ) : (
          <>
            <ol className="history-rail__list">
              {history.conversations.map((conversation) => {
                const selected =
                  history.selectedConversation?.conversation_id ===
                  conversation.conversation_id;
                return (
                  <li key={conversation.conversation_id}>
                    <button
                      type="button"
                      className="history-rail__item"
                      aria-pressed={selected}
                      onClick={() => select(conversation)}
                    >
                      <span className="history-rail__item-title">
                        {conversationLabel(conversation.conversation_id)}
                      </span>
                      <span>
                        Activitate{" "}
                        <ServerTime value={conversation.last_activity_at} />
                      </span>
                      <span>
                        Următoarea expirare{" "}
                        <ServerTime
                          value={conversation.next_content_expires_at}
                        />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            {history.conversationsNextCursor ? (
              <Button
                variant="ghost"
                className="history-rail__more"
                onClick={() => void history.loadMoreConversations()}
              >
                Mai multe conversații
              </Button>
            ) : null}
          </>
        )}
      </div>

      <div className="session-navigation__footer">
        <span className="session-navigation__rule" aria-hidden="true" />
        <span>Core Web Design</span>
        <strong className="is-mono">v1.0.0</strong>
      </div>
    </div>
  );
};

const EntryMetadata = ({
  entry,
}: {
  entry: AvailableConversationEntry;
}) => (
  <dl className="history-entry__metadata">
    <div>
      <dt>Creat</dt>
      <dd>
        <ServerTime value={entry.created_at} />
      </dd>
    </div>
    <div>
      <dt>Expiră</dt>
      <dd>
        <ServerTime value={entry.content_expires_at} />
      </dd>
    </div>
    <div>
      <dt>Secvență</dt>
      <dd className="is-mono">{entry.entry_sequence}</dd>
    </div>
  </dl>
);

const HistoryEntry = ({
  entry,
  history,
}: {
  entry: AvailableConversationEntry;
  history: ConversationHistoryController;
}) => {
  const [confirming, setConfirming] = useState(false);
  const actionRef = useRef<HTMLButtonElement>(null);
  const disposition = history.deletionOutcomes[entry.entry_id];
  const deleting = history.deletingEntryId === entry.entry_id;
  const errorCode = history.deletionErrorCodes[entry.entry_id];

  useEffect(() => {
    if (disposition) {
      actionRef.current?.focus({ preventScroll: true });
    }
  }, [disposition]);

  const deleteEntry = async () => {
    await history.requestDeletion(entry.entry_id);
    setConfirming(false);
    window.requestAnimationFrame(() => {
      actionRef.current?.focus({ preventScroll: true });
    });
  };

  const absorbDisabledClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <li
      className={`history-entry history-entry--${entry.role.toLowerCase()}`}
      data-entry-sequence={entry.entry_sequence}
    >
      <header className="history-entry__header">
        <div>
          <span className="history-entry__role">
            {historyRoleLabel(entry.role)}
          </span>
          {entry.content_kind === "COUNTEROPINION_RESULT" ? (
            <span className="history-entry__kind">Contraopinie</span>
          ) : null}
        </div>
        <code>#{entry.entry_sequence}</code>
      </header>

      {disposition ? (
        <div className="history-entry__deleted" role="status">
          <Trash2 size={18} aria-hidden="true" />
          <div>
            <strong>
              {disposition === "REQUESTED"
                ? "Ștergere solicitată"
                : "Ștergerea era deja solicitată"}
            </strong>
            <p>
              Conținutul nu mai este afișat în browser. Eliminarea fizică
              urmează politica aplicabilă.
            </p>
          </div>
        </div>
      ) : (
        <p className="history-entry__content">{entry.content}</p>
      )}

      <EntryMetadata entry={entry} />

      <div className="history-entry__actions">
        {confirming && !disposition ? (
          <div className="history-entry__confirmation" role="alert">
            <div>
              <strong>Ștergi anticipat această intrare?</strong>
              <p>
                Cererea este auditabilă și nu modifică termenele celorlalte
                intrări.
              </p>
            </div>
            <Button
              variant="ghost"
              disabled={deleting}
              onClick={() => setConfirming(false)}
            >
              Păstrează mesajul
            </Button>
          </div>
        ) : null}
        <Button
          ref={actionRef}
          variant={confirming ? "destructive" : "ghost"}
          className="history-entry__delete"
          busy={deleting}
          aria-disabled={disposition ? "true" : undefined}
          aria-label={
            disposition
              ? `Ștergerea intrării ${entry.entry_sequence} a fost solicitată`
              : confirming
                ? `Confirmă ștergerea intrării ${entry.entry_sequence}`
                : `Șterge intrarea ${entry.entry_sequence}`
          }
          onClick={
            disposition
              ? absorbDisabledClick
              : confirming
                ? () => void deleteEntry()
                : () => setConfirming(true)
          }
        >
          <Trash2 size={16} aria-hidden="true" />
          {disposition
            ? "Ștergere solicitată"
            : confirming
              ? "Confirmă ștergerea"
              : "Șterge anticipat"}
        </Button>
      </div>
      {errorCode ? (
        <div className="history-entry__error" role="alert">
          <span>{historyFailureMessage(errorCode)}</span>
          <code>{errorCode}</code>
        </div>
      ) : null}
    </li>
  );
};

export const ConversationHistoryView = ({
  history,
  canResume,
}: {
  history: ConversationHistoryController;
  canResume: boolean;
}) => {
  const conversation = history.selectedConversation;

  if (!conversation) {
    return null;
  }

  const availableEntries = history.entries.filter(
    (entry) => entry.state === "AVAILABLE",
  ).length;

  return (
    <section className="history-view" aria-labelledby="history-view-title">
      <header className="history-view__header">
        <div>
          <p className="eyebrow">Istoric criptat</p>
          <h2 id="history-view-title">
            {conversationLabel(conversation.conversation_id)}
          </h2>
          <p>
            Citirea nu schimbă termenul niciunei intrări și nu activează
            conversația în sesiunea curentă.
          </p>
        </div>
        <Button
          variant="ghost"
          className="button--icon"
          aria-label="Închide conversația"
          onClick={history.closeConversation}
        >
          <X size={20} aria-hidden="true" />
        </Button>
      </header>

      <dl className="history-view__summary">
        <div>
          <dt>Ultima activitate</dt>
          <dd>
            <ServerTime value={conversation.last_activity_at} />
          </dd>
        </div>
        <div>
          <dt>Următoarea expirare</dt>
          <dd>
            <ServerTime value={conversation.next_content_expires_at} />
          </dd>
        </div>
        <div>
          <dt>Interval vizibil</dt>
          <dd className="is-mono">
            {conversation.first_visible_sequence}–
            {conversation.last_visible_sequence}
          </dd>
        </div>
      </dl>

      {history.entriesState === "loading" && history.entries.length === 0 ? (
        <div className="history-view__loading" role="status">
          <Clock3 size={20} aria-hidden="true" />
          <div>
            <strong>Citire autorizată în curs</strong>
            <span>
              API-ul verifică din nou ownerul și sesiunea înainte de
              decriptare.
            </span>
          </div>
        </div>
      ) : history.entriesState === "error" ? (
        <Failure
          code={history.entriesErrorCode}
          onRetry={() => void history.selectConversation(conversation)}
        />
      ) : (
        <ol className="history-view__entries">
          {history.entries.map((entry) =>
            entry.state === "REMOVED" ? (
              <li
                className="history-entry-removed"
                key={`removed-${entry.after_sequence}-${entry.before_sequence}`}
              >
                <span aria-hidden="true" />
                <div>
                  <strong>
                    Mesajele mai vechi au fost eliminate conform politicii
                  </strong>
                  <code>
                    după {entry.after_sequence} · înainte de{" "}
                    {entry.before_sequence}
                  </code>
                </div>
              </li>
            ) : (
              <HistoryEntry
                key={entry.entry_id}
                entry={entry}
                history={history}
              />
            ),
          )}
        </ol>
      )}

      {history.entriesNextCursor ? (
        <Button
          variant="secondary"
          className="history-view__more"
          onClick={() => void history.loadMoreEntries()}
        >
          Mai multe mesaje
        </Button>
      ) : null}

      <div className="history-resume">
        <div className="history-resume__copy">
          <RotateCcw size={18} aria-hidden="true" />
          <div>
            <strong>Reia lucrul</strong>
            <p>
              Reluarea este distinctă de citire: creează un intent scurt, apoi
              verifică din nou identitatea, drepturile și politica la
              trimiterea mesajului nou.
            </p>
          </div>
        </div>
        {history.resumeState === "ready" && history.resume ? (
          <div className="history-resume__outcome" role="status">
            <div>
              <strong>Reluare pregătită</strong>
              <span>
                Intent valabil până la{" "}
                <ServerTime value={history.resume.resume_expires_at} />.
              </span>
            </div>
            <Button variant="ghost" onClick={() => history.clearResume()}>
              Renunță
            </Button>
          </div>
        ) : (
          <Button
            variant="secondary"
            busy={history.resumeState === "preparing"}
            disabled={
              !canResume ||
              history.entriesState !== "ready" ||
              availableEntries === 0
            }
            onClick={() => void history.prepareResume()}
          >
            <RotateCcw size={16} aria-hidden="true" />
            {history.resumeState === "preparing"
              ? "Pregătim reluarea"
              : "Pregătește reluarea"}
          </Button>
        )}
        {!canResume ? (
          <p className="history-resume__hint">
            Reluarea devine disponibilă într-o sesiune autorizată fără rulare
            selectată.
          </p>
        ) : null}
        {history.resumeState === "error" ? (
          <div className="history-resume__error" role="alert">
            <span>{historyFailureMessage(history.resumeErrorCode)}</span>
            {history.resumeErrorCode ? (
              <code>{history.resumeErrorCode}</code>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
};
